// src/pages/editor-tasks/EditorTasksWithDesigners.js
import React, { useState, useEffect, useContext } from 'react';
import Layout from '../../components/layout/Layout';
import { ThemeContext } from '../../context/ThemeContext';
import { API_BASE } from '../../api';

function EditorTasksWithDesigners() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAdminNotesModal, setShowAdminNotesModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [thumbnailLink, setThumbnailLink] = useState('');
  const [designerNotes, setDesignerNotes] = useState('');
  const [designerStatus, setDesignerStatus] = useState('in_progress');
  const [adminNotes, setAdminNotes] = useState('');
  const { currentTheme } = useContext(ThemeContext);


  // Colors
  const colors = {
    backgroundDark: '#0f0c29',
    backgroundMedium: '#302b63',
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: currentTheme?.primary || '#6366F1',
    secondary: currentTheme?.secondary || '#8B5CF6',
    accent: currentTheme?.accent || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#3B82F6'
  };

  useEffect(() => {
    fetchEditorTasksWithDesigners();
  }, []);

  const fetchEditorTasksWithDesigners = async () => {
    try {
      setLoading(true);
      setMessage('');
      
      const response = await fetch(`${API_BASE}/get_editor_tasks_with_designers.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && Array.isArray(data.tasks)) {
        setTasks(data.tasks);
      } else {
        setTasks([]);
        setMessage('No tasks with designer assignments found');
      }
    } catch (error) {
      console.error('Error fetching editor tasks with designers:', error);
      setMessage(`❌ Error: ${error.message}`);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00' || dateString === 'NULL') return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString || dateString === '0000-00-00 00:00:00' || dateString === 'NULL') return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { bg: '#F59E0B', text: 'Pending', icon: 'fa-clock' },
      'assigned': { bg: '#6366F1', text: 'Assigned', icon: 'fa-user-check' },
      'in_progress': { bg: '#06B6D4', text: 'In Progress', icon: 'fa-spinner' },
      'completed': { bg: '#10B981', text: 'Completed', icon: 'fa-check' },
      'in_review': { bg: '#8B5CF6', text: 'In Review', icon: 'fa-eye' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span style={{
        background: config.bg,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '8px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <i className={`fas ${config.icon}`} style={{ fontSize: '0.6rem' }}></i>
        {config.text}
      </span>
    );
  };

  const getDesignerStatusBadge = (status) => {
    const statusConfig = {
      'pending': { bg: '#F59E0B', text: 'Pending', icon: 'fa-clock' },
      'assigned': { bg: '#6366F1', text: 'Assigned', icon: 'fa-user-check' },
      'in_progress': { bg: '#06B6D4', text: 'In Progress', icon: 'fa-spinner' },
      'completed': { bg: '#10B981', text: 'Completed', icon: 'fa-check' },
      'reviewed': { bg: '#8B5CF6', text: 'Reviewed', icon: 'fa-eye' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span style={{
        background: config.bg,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '8px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <i className={`fas ${config.icon}`} style={{ fontSize: '0.6rem' }}></i>
        {config.text}
      </span>
    );
  };

  const getVideoTypeBadge = (videoType) => {
    const types = {
      'indoor_reel': { color: '#6366F1', label: 'Indoor Reel', icon: 'fa-home' },
      'outdoor_reel': { color: '#10B981', label: 'Outdoor Reel', icon: 'fa-tree' },
      'podcast': { color: '#8B5CF6', label: 'Podcast', icon: 'fa-podcast' },
      'indoor_long_video': { color: '#F59E0B', label: 'Indoor Long', icon: 'fa-video' },
      'outdoor_long_video': { color: '#06B6D4', label: 'Outdoor Long', icon: 'fa-mountain' }
    };
    
    const type = types[videoType] || { color: '#6B7280', label: 'Unknown', icon: 'fa-question' };
    
    return (
      <span style={{
        background: type.color,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '8px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <i className={`fas ${type.icon}`} style={{ fontSize: '0.6rem' }}></i>
        {type.label}
      </span>
    );
  };

  const openLink = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleViewWork = (task) => {
    setSelectedTask(task);
    setThumbnailLink(task.thumbnail_link || '');
    setDesignerNotes(task.designer_notes || '');
    setDesignerStatus(task.designer_status || 'in_progress');
    setShowModal(true);
  };

  const handleViewAdminNotes = (task) => {
    setSelectedTask(task);
    setAdminNotes(task.admin_notes || '');
    setShowAdminNotesModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTask(null);
    setThumbnailLink('');
    setDesignerNotes('');
    setDesignerStatus('in_progress');
    setUploading(false);
  };

  const closeAdminNotesModal = () => {
    setShowAdminNotesModal(false);
    setSelectedTask(null);
    setAdminNotes('');
  };

  const handleSubmitWork = async () => {
    if (!selectedTask) return;
    
    try {
      setUploading(true);
      
      const designerId = sessionStorage.getItem('userId');
      
      const updateData = {
        task_id: selectedTask.id,
        designer_id: designerId,
        designer_status: designerStatus,
        thumbnail_link: thumbnailLink.trim(),
        designer_notes: designerNotes.trim()
      };
      
      // Remove empty fields
      if (updateData.thumbnail_link === '') {
        updateData.thumbnail_link = null;
      }
      if (updateData.designer_notes === '') {
        updateData.designer_notes = null;
      }
      
      const response = await fetch(`${API_BASE}/update_designer_work.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMessage('✅ Designer work updated successfully!');
        
        // Update the task in local state
        setTasks(prevTasks => 
          prevTasks.map(task => 
            task.id === selectedTask.id 
              ? { ...task, 
                  thumbnail_link: updateData.thumbnail_link || null,
                  designer_notes: updateData.designer_notes || null,
                  designer_status: designerStatus
                }
              : task
          )
        );
        
        // Close modal after successful update
        setTimeout(() => {
          closeModal();
          fetchEditorTasksWithDesigners(); // Refresh the list
        }, 1500);
      } else {
        throw new Error(data.message || 'Failed to update work');
      }
    } catch (error) {
      console.error('Error updating designer work:', error);
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Filter tasks based on search term
  const filteredTasks = tasks.filter(task => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      (task.video_name && task.video_name.toLowerCase().includes(searchLower)) ||
      (task.client_name && task.client_name.toLowerCase().includes(searchLower)) ||
      (task.created_by_name && task.created_by_name.toLowerCase().includes(searchLower)) ||
      (task.status && task.status.toLowerCase().includes(searchLower)) ||
      (task.video_type && task.video_type.toLowerCase().includes(searchLower)) ||
      (task.designer_status && task.designer_status.toLowerCase().includes(searchLower)) ||
      (task.designer_notes && task.designer_notes.toLowerCase().includes(searchLower)) ||
      (task.admin_notes && task.admin_notes && task.admin_notes.toLowerCase().includes(searchLower))
    );
  });

  if (loading) {
    return (
      <Layout>
        <div style={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            color: colors.textLight,
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              border: `2px solid ${colors.primary}`,
              borderTop: `2px solid transparent`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            Loading editor tasks with designers...
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
        padding: '20px'
      }}>
        {/* Header */}
        <div style={{
          marginBottom: '30px',
          padding: '20px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '15px',
          border: `1px solid ${colors.borderDark}`
        }}>
          <h1 style={{
            color: 'white',
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <i className="fas fa-users-cog"></i>
            Editor Tasks with Designers
          </h1>
          <p style={{ color: colors.textLight }}>
            View all editor tasks assigned to designers
          </p>
          
          <div style={{
            display: 'flex',
            gap: '15px',
            marginTop: '15px',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <button
              onClick={fetchEditorTasksWithDesigners}
              disabled={loading}
              style={{
                background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: loading ? 0.7 : 1
              }}
            >
              <i className={`fas fa-sync ${loading ? 'fa-spin' : ''}`}></i>
              Refresh
            </button>
            
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '8px 16px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-layer-group" style={{ color: colors.primary }}></i>
              <span style={{ color: colors.textLight }}>
                Total: <strong style={{ color: 'white' }}>{tasks.length}</strong>
              </span>
            </div>
            
            {/* Search Input */}
            <div style={{ flex: 1, maxWidth: '300px' }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: `1px solid ${colors.borderDark}`,
                    color: 'white',
                    padding: '8px 35px 8px 12px',
                    borderRadius: '8px',
                    fontSize: '0.85rem'
                  }}
                />
                <i className="fas fa-search" style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: colors.textLight,
                  fontSize: '0.85rem'
                }}></i>
              </div>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div style={{
            marginBottom: '20px',
            padding: '15px',
            borderRadius: '10px',
            background: message.includes('✅') 
              ? `rgba(16, 185, 129, 0.2)` 
              : message.includes('❌')
              ? `rgba(239, 68, 68, 0.2)`
              : `rgba(59, 130, 246, 0.2)`,
            border: `1px solid ${message.includes('✅') ? colors.success : message.includes('❌') ? colors.danger : colors.primary}`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className={`fas ${message.includes('✅') ? 'fa-check-circle' : message.includes('❌') ? 'fa-exclamation-circle' : 'fa-info-circle'}`}
                 style={{ 
                   color: message.includes('✅') ? colors.success : 
                          message.includes('❌') ? colors.danger : colors.primary 
                 }}></i>
              <span>{message}</span>
            </div>
            <button
              onClick={() => setMessage('')}
              style={{
                background: 'none',
                border: 'none',
                color: colors.textLight,
                cursor: 'pointer'
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Tasks Table */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '15px',
          border: `1px solid ${colors.borderDark}`,
          overflow: 'hidden'
        }}>
          <div style={{
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            color: 'white',
            padding: '15px 20px',
            fontWeight: '600',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className="fas fa-table"></i>
              Editor Tasks with Designers Table
            </div>
            <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
              Showing {filteredTasks.length} of {tasks.length} tasks
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              color: 'white',
              fontSize: '0.85rem'
            }}>
              <thead>
                <tr style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderBottom: `2px solid ${colors.borderDark}`
                }}>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>ID</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Video Name</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Type</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Client</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Editor Status</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Designer Status</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Thumbnail Link</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Designer Notes</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Admin Notes</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Video Count</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Shoot Date</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Created By</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Raw Link</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Work</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.length === 0 ? (
                  <tr>
                    <td colSpan="14" style={{ padding: '40px', textAlign: 'center', color: colors.textLight }}>
                      <i className="fas fa-search fa-2x" style={{ marginBottom: '15px', opacity: 0.5 }}></i>
                      <div>No tasks with designer assignments found</div>
                      {searchTerm && (
                        <div style={{ marginTop: '10px', fontSize: '0.9rem' }}>
                          No tasks match your search: "<strong>{searchTerm}</strong>"
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredTasks.map((task) => (
                    <tr key={task.id} style={{
                      borderBottom: `1px solid ${colors.borderDark}`,
                      transition: 'background 0.3s'
                    }}>
                      <td style={{ padding: '12px 15px', fontWeight: '600' }}>
                        #{task.id}
                      </td>
                      <td style={{ padding: '12px 15px', fontWeight: '500' }}>
                        {task.video_name}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {getVideoTypeBadge(task.video_type)}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {task.client_name || 'N/A'}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {getStatusBadge(task.status)}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {getDesignerStatusBadge(task.designer_status)}
                      </td>
                      <td style={{ padding: '12px 15px', maxWidth: '150px' }}>
                        {task.thumbnail_link ? (
                          <button
                            onClick={() => openLink(task.thumbnail_link)}
                            style={{
                              background: `linear-gradient(135deg, ${colors.accent}, #22D3EE)`,
                              color: 'white',
                              border: 'none',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.7rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '100%'
                            }}
                            title={task.thumbnail_link}
                          >
                            <i className="fas fa-external-link-alt"></i>
                            View Thumbnail
                          </button>
                        ) : (
                          <span style={{ 
                            color: colors.textLight, 
                            fontSize: '0.8rem',
                            fontStyle: 'italic'
                          }}>
                            No thumbnail
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 15px', maxWidth: '150px' }}>
                        <div style={{
                          maxHeight: '60px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: '1.2',
                          color: colors.textLight,
                          fontSize: '0.8rem'
                        }}>
                          {task.designer_notes || 'No notes'}
                        </div>
                      </td>
                      <td style={{ padding: '12px 15px', maxWidth: '150px' }}>
                        <div style={{
                          maxHeight: '60px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: '1.2',
                          color: colors.textLight,
                          fontSize: '0.8rem'
                        }}>
                          {task.admin_notes || 'No admin notes'}
                        </div>
                        {task.admin_notes && (
                          <button
                            onClick={() => handleViewAdminNotes(task)}
                            style={{
                              background: `rgba(255, 255, 255, 0.1)`,
                              color: colors.textLight,
                              border: `1px solid ${colors.borderDark}`,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.7rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              width: '100%',
                              justifyContent: 'center',
                              marginTop: '4px'
                            }}
                            title="View Admin Notes"
                          >
                            <i className="fas fa-eye"></i>
                            View Notes
                          </button>
                        )}
                      </td>
                      <td style={{ padding: '12px 15px', textAlign: 'center', fontWeight: '600' }}>
                        {task.video_count || 1}
                      </td>
                      <td style={{ padding: '12px 15px', color: colors.textLight }}>
                        {formatDate(task.shoot_date)}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <i className="fas fa-user-plus" style={{ color: colors.accent, fontSize: '0.8rem' }}></i>
                          <span style={{ color: colors.accent, fontWeight: '500' }}>
                            {task.created_by_name || 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {task.raw_link ? (
                          <button
                            onClick={() => openLink(task.raw_link)}
                            style={{
                              background: `linear-gradient(135deg, ${colors.info}, #60A5FA)`,
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                            title={task.raw_link}
                          >
                            <i className="fas fa-external-link-alt"></i>
                            View
                          </button>
                        ) : (
                          <span style={{ color: colors.textLight, fontSize: '0.8rem' }}>N/A</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        <button
                          onClick={() => handleViewWork(task)}
                          style={{
                            background: `linear-gradient(135deg, ${colors.success}, #059669)`,
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            width: '100%',
                            justifyContent: 'center'
                          }}
                          title="Update Designer Work"
                        >
                          <i className="fas fa-briefcase"></i>
                          Work
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Simplified Work Modal - Update Designer Work */}
      {showModal && selectedTask && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 100%)`,
            borderRadius: '15px',
            border: `1px solid ${colors.borderDark}`,
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            {/* Modal Header */}
            <div style={{
              background: `linear-gradient(135deg, ${colors.success}, #059669)`,
              color: 'white',
              padding: '20px',
              borderRadius: '15px 15px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="fas fa-briefcase"></i>
                Update Designer Work
              </h3>
              <button
                onClick={closeModal}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: 'white',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Modal Body - Simplified */}
            <div style={{ padding: '20px' }}>
              {/* Task Info */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '10px',
                padding: '15px',
                marginBottom: '20px',
                border: `1px solid ${colors.borderDark}`
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '10px'
                }}>
                  <div>
                    <span style={{ 
                      color: colors.textLight, 
                      fontSize: '0.85rem',
                      display: 'block'
                    }}>
                      Task #{selectedTask.id}
                    </span>
                    <span style={{ 
                      color: 'white', 
                      fontWeight: '500',
                      fontSize: '1.1rem'
                    }}>
                      {selectedTask.video_name}
                    </span>
                  </div>
                  <div>
                    {getDesignerStatusBadge(selectedTask.designer_status)}
                  </div>
                </div>
                <div style={{ 
                  color: colors.textLight, 
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="fas fa-user-tie"></i>
                  {selectedTask.client_name || 'No client'}
                </div>
              </div>

              {/* Designer Status Dropdown */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '10px',
                padding: '15px',
                marginBottom: '15px',
                border: `1px solid ${colors.borderDark}`
              }}>
                <label style={{ 
                  color: colors.textLight, 
                  display: 'block', 
                  marginBottom: '10px',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  <i className="fas fa-chart-line" style={{ marginRight: '8px' }}></i>
                  Designer Status
                </label>
                <select
                  value={designerStatus}
                  onChange={(e) => setDesignerStatus(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: 'rgba(255, 255, 255, 0.07)',
                    border: `2px solid ${colors.borderDark}`,
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  <option value="pending" style={{ background: colors.backgroundDark, color: 'white' }}>Pending</option>
                  <option value="in_progress" style={{ background: colors.backgroundDark, color: 'white' }}>In Progress</option>
                  <option value="completed" style={{ background: colors.backgroundDark, color: 'white' }}>Completed</option>
                  <option value="reviewed" style={{ background: colors.backgroundDark, color: 'white' }}>Reviewed</option>
                </select>
              </div>

              {/* Thumbnail Link Input */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '10px',
                padding: '15px',
                marginBottom: '15px',
                border: `1px solid ${colors.borderDark}`
              }}>
                <label style={{ 
                  color: colors.textLight, 
                  display: 'block', 
                  marginBottom: '10px',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  <i className="fas fa-image" style={{ marginRight: '8px' }}></i>
                  Thumbnail Link (Optional)
                </label>
                <input
                  type="text"
                  value={thumbnailLink}
                  onChange={(e) => setThumbnailLink(e.target.value)}
                  placeholder="https://drive.google.com/file/d/... or https://i.imgur.com/..."
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: 'rgba(255, 255, 255, 0.07)',
                    border: `2px solid ${colors.borderDark}`,
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '0.9rem'
                  }}
                />
                {selectedTask.thumbnail_link && (
                  <div style={{ 
                    marginTop: '8px',
                    fontSize: '0.8rem',
                    color: colors.textLight
                  }}>
                    <div style={{ opacity: 0.7 }}>Current:</div>
                    <div style={{
                      color: colors.success,
                      wordBreak: 'break-all',
                      fontSize: '0.75rem',
                      padding: '4px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '4px',
                      marginTop: '4px'
                    }}>
                      {selectedTask.thumbnail_link}
                    </div>
                  </div>
                )}
              </div>

              {/* Designer Notes Input */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '10px',
                padding: '15px',
                marginBottom: '20px',
                border: `1px solid ${colors.borderDark}`
              }}>
                <label style={{ 
                  color: colors.textLight, 
                  display: 'block', 
                  marginBottom: '10px',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  <i className="fas fa-sticky-note" style={{ marginRight: '8px' }}></i>
                  Designer Notes (Optional)
                </label>
                <textarea
                  value={designerNotes}
                  onChange={(e) => setDesignerNotes(e.target.value)}
                  placeholder="Enter your notes here..."
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: 'rgba(255, 255, 255, 0.07)',
                    border: `2px solid ${colors.borderDark}`,
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '0.9rem',
                    resize: 'vertical',
                    minHeight: '80px'
                  }}
                />
                {selectedTask.designer_notes && (
                  <div style={{ 
                    marginTop: '8px',
                    fontSize: '0.8rem',
                    color: colors.textLight
                  }}>
                    <div style={{ opacity: 0.7 }}>Current Notes:</div>
                    <div style={{
                      color: colors.textLight,
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.75rem',
                      padding: '4px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '4px',
                      marginTop: '4px',
                      maxHeight: '60px',
                      overflow: 'auto'
                    }}>
                      {selectedTask.designer_notes}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '10px',
                marginTop: '20px'
              }}>
                <button
                  onClick={handleSubmitWork}
                  disabled={uploading}
                  style={{
                    background: `linear-gradient(135deg, ${colors.success}, #059669)`,
                    color: 'white',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    opacity: uploading ? 0.7 : 1,
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    flex: 2,
                    justifyContent: 'center',
                    fontWeight: '600'
                  }}
                >
                  {uploading ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: `2px solid white`,
                        borderTop: `2px solid transparent`,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      Update Work
                    </>
                  )}
                </button>
                
                <button
                  onClick={closeModal}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: colors.textLight,
                    border: `1px solid ${colors.borderDark}`,
                    padding: '12px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flex: 1
                  }}
                >
                  <i className="fas fa-times"></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Notes Modal */}
      {showAdminNotesModal && selectedTask && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 100%)`,
            borderRadius: '15px',
            border: `1px solid ${colors.borderDark}`,
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            {/* Modal Header */}
            <div style={{
              background: `linear-gradient(135deg, ${colors.info}, #3B82F6)`,
              color: 'white',
              padding: '20px',
              borderRadius: '15px 15px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="fas fa-sticky-note"></i>
                Admin Notes - Task #{selectedTask.id}
              </h3>
              <button
                onClick={closeAdminNotesModal}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: 'white',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px' }}>
              {/* Task Info */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '10px',
                padding: '15px',
                marginBottom: '20px',
                border: `1px solid ${colors.borderDark}`
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '10px'
                }}>
                  <div>
                    <span style={{ 
                      color: colors.textLight, 
                      fontSize: '0.85rem',
                      display: 'block'
                    }}>
                      Task #{selectedTask.id}
                    </span>
                    <span style={{ 
                      color: 'white', 
                      fontWeight: '500',
                      fontSize: '1.1rem'
                    }}>
                      {selectedTask.video_name}
                    </span>
                  </div>
                  <div>
                    {getDesignerStatusBadge(selectedTask.designer_status)}
                  </div>
                </div>
                <div style={{ 
                  color: colors.textLight, 
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="fas fa-user-tie"></i>
                  {selectedTask.client_name || 'No client'}
                </div>
              </div>

              {/* Admin Notes Display */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '10px',
                padding: '15px',
                marginBottom: '20px',
                border: `1px solid ${colors.borderDark}`
              }}>
                <label style={{ 
                  color: colors.textLight, 
                  display: 'block', 
                  marginBottom: '10px',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  <i className="fas fa-sticky-note" style={{ marginRight: '8px' }}></i>
                  Admin Notes
                </label>
                <div style={{
                  padding: '15px',
                  background: 'rgba(255, 255, 255, 0.07)',
                  border: `1px solid ${colors.borderDark}`,
                  borderRadius: '8px',
                  color: colors.textLight,
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.9rem',
                  minHeight: '120px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  lineHeight: '1.5'
                }}>
                  {adminNotes || 'No admin notes available for this task.'}
                </div>
                <div style={{ 
                  fontSize: '0.8rem', 
                  color: colors.textLight, 
                  marginTop: '8px',
                  opacity: 0.7,
                  fontStyle: 'italic'
                }}>
                  <i className="fas fa-info-circle"></i> 
                  These notes are from the admin/manager for this task.
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '10px',
                marginTop: '20px'
              }}>
                <button
                  onClick={closeAdminNotesModal}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: colors.textLight,
                    border: `1px solid ${colors.borderDark}`,
                    padding: '12px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flex: 1,
                    justifyContent: 'center'
                  }}
                >
                  <i className="fas fa-times"></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        tr:hover {
          background: rgba(255, 255, 255, 0.03) !important;
        }
        
        select:focus {
          outline: none;
          border-color: ${colors.primary} !important;
          box-shadow: 0 0 0 2px ${colors.primary}20;
        }
        
        select option {
          background: ${colors.backgroundDark};
          color: white;
          padding: 8px;
        }
        
        select option:checked {
          background: ${colors.primary};
          color: white;
        }
        
        input:focus {
          outline: none;
          border-color: ${colors.primary} !important;
          box-shadow: 0 0 0 2px ${colors.primary}20;
        }
        
        textarea:focus {
          outline: none;
          border-color: ${colors.primary} !important;
          box-shadow: 0 0 0 2px ${colors.primary}20;
        }
      `}</style>
    </Layout>
  );
}

export default EditorTasksWithDesigners;